import { authLog } from "./auth-log";
import { DEV_BYPASS_BEARER, isDevAuthBypass, isDevPageCheck } from "./devAuthBypass";
import { recordHubSupportConsoleLine } from "./installHubSupportConsoleCapture";
import { hubOperationalDataSourceRequestHeader } from "./hubDataSource";
import { hubApiUrl } from "./hubApiOrigin";
import { hubApiAuthHeader } from "./hubApiAuthHeader";
import { standardUserPreviewRequestHeader } from "./standardUserPreview";
import { hubViewAsRequestHeader } from "./hubViewAs";
import { mergeAbortSignals, takeHubQueryAbortSignal } from "./hubQueryAbortSignal";
import {
  acquireApiFetchPermit,
  type ApiFetchPriority,
} from "./apiFetchAdmission";
import {
  ApiDeadlineError,
  DEFAULT_REST_HEADER_DEADLINE_MS,
  DEFAULT_REST_TOTAL_DEADLINE_MS,
  fetchWithDeadlines,
  type ApiFetchDeadlineOptions,
} from "./apiFetchDeadline";
import {
  beginApiFetchTimeline,
  finishApiFetchTimeline,
  updateApiFetchTimeline,
  type ApiFetchTimeline,
} from "./apiFetchTelemetry";
import {
  beginSlowApiRequestWatch,
  isAuthServiceDegraded,
  reportAuthServiceDegraded,
  reportAuthServiceRecovered,
} from "./hubBackendDegraded";
import { createHubClientRequestId, HUB_CLIENT_REQUEST_ID_HEADER } from "./hubClientRequestId";
import { notifyLaborAttentionActivity } from "./labor/useLaborAttentionActivity";

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

/** Inline error when Apache returns 502/503/504 (brief blue-green cutover or gateway blip). */
export const HUB_GATEWAY_DOWN_MESSAGE =
  "This request did not finish. The hub was briefly unreachable — retry if needed.";

/** Production / gateway: hub request failed during deploy or an upstream blip. */
export const HUB_UPDATING_BANNER_MESSAGE =
  "The hub is temporarily unreachable — retry when this clears.";

/** Shown when fetch never got a response (network drop or connection reset). */
export const HUB_UNREACHABLE_MESSAGE =
  "Could not reach the hub. Check your connection and try again.";

/** Vite dev proxy JSON `message` while the local Fastify process is restarting. */
export const HUB_LOCAL_API_RESTARTING_MESSAGE = "Local API is restarting";

const DEV_API_FETCH_MAX_ATTEMPTS = 15;
const PROD_INFRA_FETCH_MAX_ATTEMPTS = 4;
const AUTH_UNAVAILABLE_FETCH_MAX_ATTEMPTS = 4;

/**
 * API body `error` code when Supabase Auth could not be reached to verify the
 * session (HTTP 503). The auth preHandler rejected the request before any route
 * handler ran, so retrying is safe for every method — nothing was executed.
 */
export const AUTH_SERVICE_UNAVAILABLE_ERROR_CODE = "auth_service_unavailable";

export function isAuthServiceUnavailablePayload(status: number, data: unknown): boolean {
  if (status !== 503 || !data || typeof data !== "object") {
    return false;
  }
  return (data as Record<string, unknown>).error === AUTH_SERVICE_UNAVAILABLE_ERROR_CODE;
}

export function authServiceUnavailableRetryAfterSeconds(data: unknown): number {
  if (data && typeof data === "object") {
    const raw = (data as Record<string, unknown>).retryAfterSeconds;
    if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
      return Math.round(raw);
    }
  }
  return 15;
}

function devApiFetchRetryDelay(attemptIndex: number): number {
  return Math.min(400 * 1.5 ** attemptIndex, 3_000);
}

function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

/** Apache / gateway error pages arrive as an HTML document, not our JSON shape. */
function looksLikeHtml(text: string): boolean {
  const t = text.trimStart();
  return /^<!doctype\s+html/i.test(t) || /^<html[\s>]/i.test(t);
}

/** Statuses a reverse proxy returns when the upstream API is briefly gone (deploy). */
export function isGatewayDownStatus(status: number): boolean {
  return status === 0 || status === 502 || status === 503 || status === 504;
}

/** Dev Vite proxy 503 while tsx watch reloads the local API (not a production outage). */
export function isLocalDevApiRestartPayload(status: number, data: unknown): boolean {
  if (status === 0) {
    return true;
  }
  if (status === 502 || status === 504) {
    return true;
  }
  if (status === 503) {
    if (!data || typeof data !== "object") {
      return true;
    }
    const body = data as Record<string, unknown>;
    const message = typeof body.message === "string" ? body.message.trim() : "";
    const error = typeof body.error === "string" ? body.error.trim() : "";
    return (
      message === HUB_LOCAL_API_RESTARTING_MESSAGE ||
      (message === "" && error === "Service Unavailable")
    );
  }
  if (status === 500) {
    return isGenericInfrastructure500(status, data, parseApiErrorPayload(status, data).message);
  }
  return false;
}

export function isLocalDevApiRestartResponse(status: number, data: unknown): boolean {
  if (!import.meta.env?.DEV) {
    return false;
  }
  return isLocalDevApiRestartPayload(status, data);
}

function shouldRetryDevApiFetch(status: number, data: unknown): boolean {
  return isLocalDevApiRestartResponse(status, data);
}

function prodInfraFetchRetryDelay(attemptIndex: number): number {
  return Math.min(200 * 2 ** attemptIndex, 2_000);
}

/** Production blue-green cutover: retry only idempotent reads on gateway blips. */
export function isProductionInfraFetchRetryCandidate(
  method: string,
  status: number,
  data: unknown,
  attempt: number,
  maxAttempts: number,
): boolean {
  const normalizedMethod = method.toUpperCase();
  if (normalizedMethod !== "GET" && normalizedMethod !== "HEAD") {
    return false;
  }
  if (attempt >= maxAttempts - 1) {
    return false;
  }
  if (status === 0) {
    return true;
  }
  return isProductionInfrastructureFailure(status, data);
}

export function shouldRetryProductionInfraFetch(
  method: string,
  status: number,
  data: unknown,
  attempt: number,
): boolean {
  if (import.meta.env.DEV) {
    return false;
  }
  return isProductionInfraFetchRetryCandidate(
    method,
    status,
    data,
    attempt,
    PROD_INFRA_FETCH_MAX_ATTEMPTS,
  );
}

function shouldRetryApiFetch(
  method: string,
  status: number,
  data: unknown,
  attempt: number,
  skipRetries = false,
): boolean {
  if (skipRetries) {
    return false;
  }
  // Auth preHandler 503: no handler ran, so any method may retry (dev and prod).
  if (
    isAuthServiceUnavailablePayload(status, data) &&
    attempt < AUTH_UNAVAILABLE_FETCH_MAX_ATTEMPTS - 1
  ) {
    return true;
  }
  if (shouldRetryDevApiFetch(status, data) && attempt < DEV_API_FETCH_MAX_ATTEMPTS - 1) {
    return true;
  }
  return shouldRetryProductionInfraFetch(method, status, data, attempt);
}

function apiFetchRetryDelay(attempt: number): number {
  if (import.meta.env.DEV) {
    return devApiFetchRetryDelay(attempt);
  }
  return prodInfraFetchRetryDelay(attempt);
}

function apiFetchMaxAttempts(): number {
  return import.meta.env.DEV ? DEV_API_FETCH_MAX_ATTEMPTS : PROD_INFRA_FETCH_MAX_ATTEMPTS;
}

/**
 * Local Vite proxy and some gateways return 500 with a generic body while the API
 * process is restarting. Real app bugs usually include a specific `message` field.
 */
export function isGenericInfrastructure500(status: number, data: unknown, resolvedMessage: string): boolean {
  if (status !== 500) {
    return false;
  }
  if (resolvedMessage === `Request failed: ${status}`) {
    return true;
  }
  if (resolvedMessage !== "Internal Server Error") {
    return false;
  }
  if (!data || typeof data !== "object") {
    return true;
  }
  const body = data as Record<string, unknown>;
  const detailMessage = typeof body.message === "string" ? body.message.trim() : "";
  return detailMessage === "" || detailMessage === "Internal Server Error";
}

/** Dev `/api/me` and similar calls while the local API or Vite proxy is restarting. */
export function isTransientHubFetchError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  if (isGatewayDownStatus(error.status)) {
    return true;
  }
  if (error.status === 500 && error.message === HUB_GATEWAY_DOWN_MESSAGE) {
    return true;
  }
  return false;
}

export function hubQueryRetry(failureCount: number, error: unknown): boolean {
  if (!import.meta.env?.DEV) {
    return false;
  }
  return isTransientHubFetchError(error) && failureCount < DEV_API_FETCH_MAX_ATTEMPTS;
}

export function hubQueryRetryDelay(attemptIndex: number): number {
  return devApiFetchRetryDelay(attemptIndex);
}

export function integratorMeQueryRetry(failureCount: number, error: unknown): boolean {
  return hubQueryRetry(failureCount, error);
}

export function integratorMeQueryRetryDelay(attemptIndex: number): number {
  return hubQueryRetryDelay(attemptIndex);
}

import { isHubOAuthBannerApiPath } from "./hub-oauth-api-path.js";

export { isHubOAuthBannerApiPath };
/**
 * Production gateway blip: show the unreachable banner and poll `/healthz`.
 * Local dev restarts use GET /dev-stack-status (written before registry reload) instead.
 */
function shouldSignalHubInfrastructureBlip(
  status: number,
  data: unknown,
  requestPath?: string,
): boolean {
  const oauthBannerPath = requestPath != null && isHubOAuthBannerApiPath(requestPath);
  if (oauthBannerPath) {
    return isGatewayDownStatus(status) || (typeof data === "string" && looksLikeHtml(data));
  }
  if (import.meta.env.DEV) {
    if (isLocalDevApiRestartResponse(status, data)) {
      return false;
    }
    if (typeof data === "string" && looksLikeHtml(data)) {
      return true;
    }
    if (status === 500) {
      if (typeof data === "string") {
        const trimmed = data.trim();
        return trimmed === "" || trimmed === "Internal Server Error";
      }
      return isGenericInfrastructure500(status, data, `Request failed: ${status}`);
    }
    return false;
  }
  return isProductionInfrastructureFailure(status, data);
}

/** Production-only infrastructure blip detection (exported for unit tests). */
export function isProductionInfrastructureFailure(status: number, data: unknown): boolean {
  if (status === 0) {
    return true;
  }
  if (typeof data === "string" && looksLikeHtml(data)) {
    return true;
  }
  if (status === 502 || status === 503 || status === 504) {
    const genericGatewayMessages = new Set([
      "Bad Gateway",
      "Service Unavailable",
      "Gateway Timeout",
    ]);
    if (typeof data === "string") {
      const message = data.trim();
      return message === "" || genericGatewayMessages.has(message);
    }
    if (!data || typeof data !== "object") {
      return true;
    }
    const body = data as Record<string, unknown>;
    const message = typeof body.message === "string" ? body.message.trim() : "";
    const error = typeof body.error === "string" ? body.error.trim() : "";
    const detail = typeof body.detail === "string" ? body.detail.trim() : "";
    if (detail !== "") {
      return false;
    }
    if (message !== "" && !genericGatewayMessages.has(message)) {
      return false;
    }
    if (error !== "" && !genericGatewayMessages.has(error)) {
      return false;
    }
    return true;
  }
  if (status === 500) {
    const resolvedMessage =
      typeof data === "string"
        ? data.trim() || `Request failed: ${status}`
        : data && typeof data === "object"
          ? (() => {
              const body = data as Record<string, unknown>;
              const message = typeof body.message === "string" ? body.message.trim() : "";
              const error = typeof body.error === "string" ? body.error.trim() : "";
              return message || error || `Request failed: ${status}`;
            })()
          : `Request failed: ${status}`;
    return isGenericInfrastructure500(status, data, resolvedMessage);
  }
  return false;
}

/** Browser event when a hub request fails like a deploy/gateway outage (not local dev restart). */
export const HUB_INFRASTRUCTURE_BLIP_EVENT = "hub:infrastructure-blip";

/** Dev-only: apiFetch is retrying while the local API or Vite proxy is restarting. */
export const HUB_DEV_STACK_RESTARTING_EVENT = "hub:dev-stack-restarting";

/** @deprecated Use {@link HUB_INFRASTRUCTURE_BLIP_EVENT}. */
export const HUB_MAYBE_UPDATING_EVENT = HUB_INFRASTRUCTURE_BLIP_EVENT;

function signalHubInfrastructureBlip(): void {
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent(HUB_INFRASTRUCTURE_BLIP_EVENT));
  }
}

function signalDevStackRestarting(): void {
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent(HUB_DEV_STACK_RESTARTING_EVENT));
  }
}

/**
 * Fastify (and many gateways) send `{ error: "Internal Server Error", message: "actual cause" }`.
 * We must prefer `message` so the UI is not stuck showing the generic title only.
 *
 * A raw HTML body means the request hit Apache, not our API (typically a deploy
 * window). Surface a calm "updating" message instead of dumping the Apache page.
 */
function parseApiErrorPayload(
  status: number,
  data: unknown,
): { message: string; detail?: string } {
  if (typeof data === "string") {
    const t = data.trim();
    if (looksLikeHtml(t)) {
      if (isGatewayDownStatus(status)) {
        return { message: HUB_GATEWAY_DOWN_MESSAGE };
      }
      return { message: `The hub returned an unexpected error (${status}). Please try again.` };
    }
    return { message: t.length > 0 ? t : `Request failed: ${status}` };
  }
  if (!data || typeof data !== "object") {
    return { message: `Request failed: ${status}` };
  }
  const o = data as Record<string, unknown>;
  const msg = typeof o.message === "string" ? o.message.trim() : "";
  const err = typeof o.error === "string" ? o.error.trim() : "";
  const detail = typeof o.detail === "string" ? o.detail.trim() : "";
  let message = msg !== "" ? msg : err !== "" ? err : `Request failed: ${status}`;
  if (isGenericInfrastructure500(status, data, message)) {
    message = HUB_GATEWAY_DOWN_MESSAGE;
  }
  return { message, detail: detail !== "" ? detail : undefined };
}

/**
 * Keep the hub support console buffer readable: a full Apache HTML error page is
 * noise, so collapse it to a one-line marker before recording.
 */
function sanitizeBodyForSupport(status: number, body: unknown): unknown {
  if (typeof body === "string" && looksLikeHtml(body)) {
    return `<gateway HTML page suppressed (HTTP ${status}; hub likely updating)>`;
  }
  return body;
}

function logApiErrorForSupport(
  path: string,
  method: string,
  status: number,
  userMessage: string,
  extras?: { detail?: string; body?: unknown; device?: Record<string, unknown> },
): void {
  const payload: Record<string, unknown> = {
    event: "api_error",
    method,
    path,
    status,
    message: userMessage,
  };
  if (extras?.detail) {
    payload.detail = extras.detail;
  }
  if (extras?.device && Object.keys(extras.device).length > 0) {
    payload.device = extras.device;
  }
  if (extras?.body !== undefined) {
    payload.responseBody = sanitizeBodyForSupport(status, extras.body);
  }
  recordHubSupportConsoleLine("error", payload);
  console.error(userMessage, payload);
}

function throwApiError(
  path: string,
  method: string,
  status: number,
  data: unknown,
  device?: Record<string, unknown>,
): never {
  const { message, detail } = parseApiErrorPayload(status, data);
  logApiErrorForSupport(path, method, status, message, { detail, body: data, device });
  if (shouldSignalHubInfrastructureBlip(status, data, path)) {
    signalHubInfrastructureBlip();
  }
  throw new ApiError(status, message, data);
}

async function authHeader(): Promise<Record<string, string>> {
  if (isDevAuthBypass() || isDevPageCheck()) {
    return {
      Authorization: `Bearer ${DEV_BYPASS_BEARER}`,
      ...(isDevPageCheck() ? { "X-Hub-Page-Check": "1" } : {}),
    };
  }
  return hubApiAuthHeader();
}

export interface ApiFetchOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  cache?: RequestCache;
  headers?: Record<string, string>;
  /** Merged into hub support console capture on API failure (for example configure serials). */
  supportLogContext?: Record<string, unknown>;
  /** Passed through to `fetch` so the request can outlive the page (for example labor clock-out). */
  keepalive?: boolean;
  /** Cancels the underlying request when the owning view or auth generation changes. */
  signal?: AbortSignal;
  /** Skip the slow-request banner (for probes that diagnose slowness themselves). */
  skipSlowRequestBanner?: boolean;
  /** Do not retry this call (mutating work that must not run twice after a gateway timeout). */
  skipRetries?: boolean;
  /** GET admission priority. User actions should use interactive; hover/prefetch uses speculative. */
  priority?: ApiFetchPriority;
  /** Maximum milliseconds from fetch call until response headers. GET/HEAD only by default. */
  headerDeadlineMs?: number;
  /** Maximum milliseconds from fetch call until the entire response body is read. */
  totalDeadlineMs?: number;
}

const REST_TOTAL_DEADLINE_OVERRIDES: ReadonlyArray<{
  prefix: string;
  totalDeadlineMs: number;
}> = [
  { prefix: "/api/mrp/forecast", totalDeadlineMs: 120_000 },
  { prefix: "/api/mrp/inventory", totalDeadlineMs: 60_000 },
  { prefix: "/api/manufacturing/batch-details", totalDeadlineMs: 60_000 },
  { prefix: "/api/shipping/paperwork/shipments", totalDeadlineMs: 60_000 },
];

export function resolveApiFetchDeadlines(
  path: string,
  method: string,
  options: Pick<ApiFetchOptions, "headerDeadlineMs" | "totalDeadlineMs">,
): ApiFetchDeadlineOptions | null {
  const normalizedMethod = method.trim().toUpperCase();
  if (normalizedMethod !== "GET" && normalizedMethod !== "HEAD" && normalizedMethod !== "") {
    if (options.headerDeadlineMs == null && options.totalDeadlineMs == null) return null;
  }
  const pathOnly = path.split("?")[0] ?? path;
  const totalOverride = REST_TOTAL_DEADLINE_OVERRIDES.find((entry) =>
    pathOnly.startsWith(entry.prefix),
  );
  return {
    headerDeadlineMs: options.headerDeadlineMs ?? DEFAULT_REST_HEADER_DEADLINE_MS,
    totalDeadlineMs:
      options.totalDeadlineMs ??
      totalOverride?.totalDeadlineMs ??
      DEFAULT_REST_TOTAL_DEADLINE_MS,
  };
}

function apiFetchBypassesAdmission(path: string, method: string): boolean {
  const normalizedMethod = method.trim().toUpperCase();
  if (normalizedMethod !== "GET" && normalizedMethod !== "HEAD" && normalizedMethod !== "") {
    return true;
  }
  const pathOnly = path.split("?")[0] ?? path;
  return (
    pathOnly === "/healthz" ||
    pathOnly === "/api/me" ||
    pathOnly.startsWith("/api/auth/") ||
    pathOnly.startsWith("/api/google-chat/")
  );
}

export function resolveApiFetchAbortSignal(
  method: string,
  optionsSignal?: AbortSignal,
): AbortSignal | undefined {
  const methodUpper = method.trim().toUpperCase();
  const isGetLike = methodUpper === "GET" || methodUpper === "HEAD" || methodUpper === "";
  return mergeAbortSignals(optionsSignal, isGetLike ? takeHubQueryAbortSignal() : undefined);
}

export function apiFetchShouldKeepalive(method: string, keepalive?: boolean): boolean {
  if (keepalive !== true) {
    return false;
  }
  const methodUpper = method.trim().toUpperCase();
  return methodUpper !== "GET" && methodUpper !== "HEAD" && methodUpper !== "";
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  // Capture before any await so a parallel query cannot steal this GET's signal.
  const signal = resolveApiFetchAbortSignal(method, options.signal);
  // "What is taking so long" messaging: report requests that outlive the
  // threshold (retries included) so users see progress instead of a bare spinner.
  const fetchStartedAtMs = Date.now();
  const clientRequestId = createHubClientRequestId();
  const skipSlowBanner =
    options.skipSlowRequestBanner === true ||
    path.split("?")[0] === "/api/hub/db-pool-holders" ||
    path.split("?")[0] === "/api/hub/db-pool-holders/stream";
  const slowWatch = beginSlowApiRequestWatch(path, fetchStartedAtMs, {
    skip: skipSlowBanner,
    method,
    clientRequestId,
  });
  const priority = options.priority ?? "normal";
  const timeline = beginApiFetchTimeline(method, path, priority);
  let releasePermit: (() => void) | undefined;
  try {
    if (!apiFetchBypassesAdmission(path, method)) {
      const permit = await acquireApiFetchPermit(priority, signal);
      releasePermit = permit.release;
      updateApiFetchTimeline(timeline, { admissionGrantedAt: permit.grantedAtMs });
    } else {
      updateApiFetchTimeline(timeline, { admissionGrantedAt: Date.now() });
    }
    return await apiFetchWithRetries<T>(
      path,
      {
        ...options,
        signal,
        headers: {
          ...options.headers,
          [HUB_CLIENT_REQUEST_ID_HEADER]: clientRequestId,
        },
      },
      timeline,
    );
  } finally {
    releasePermit?.();
    finishApiFetchTimeline(timeline);
    slowWatch.finish();
  }
}

async function apiFetchWithRetries<T>(
  path: string,
  options: ApiFetchOptions = {},
  timeline: ApiFetchTimeline,
): Promise<T> {
  const headers: Record<string, string> = {
    ...(await authHeader()),
    ...hubOperationalDataSourceRequestHeader(),
    ...standardUserPreviewRequestHeader(),
    ...hubViewAsRequestHeader(),
    ...options.headers,
  };
  const body =
    options.body !== undefined ? JSON.stringify(options.body) : undefined;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  let url = path;
  if (options.query) {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(options.query)) {
      if (value === undefined) continue;
      search.append(key, String(value));
    }
    const query = search.toString();
    if (query) url += `?${query}`;
  }
  // Dev aux stacks behind the gateway path mount (/5177/…) must not hit
  // gateway /api → 8123; use VITE_API_ORIGIN (branch API) when set.
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    const pathOnly = url.split("?")[0] ?? url;
    const queryPart = url.includes("?") ? url.slice(url.indexOf("?")) : "";
    url = `${hubApiUrl(pathOnly)}${queryPart}`;
  }

  const method = options.method ?? "GET";
  const signal = options.signal;
  const maxAttempts = apiFetchMaxAttempts();
  const deadlines = resolveApiFetchDeadlines(path, method, options);

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal?.aborted) {
      throw new DOMException("The operation was aborted.", "AbortError");
    }

    let response: Response;
    let text: string;
    try {
      const result = await fetchWithDeadlines(
        url,
        {
          method,
          headers,
          body,
          cache: options.cache ?? "default",
          ...(apiFetchShouldKeepalive(method, options.keepalive) ? { keepalive: true } : {}),
        },
        deadlines,
        signal,
        timeline,
      );
      response = result.response;
      text = result.text;
    } catch (networkError) {
      if (networkError instanceof ApiDeadlineError) {
        recordHubSupportConsoleLine("error", {
          event: "api_deadline",
          method,
          path,
          clientRequestId: headers[HUB_CLIENT_REQUEST_ID_HEADER],
          phase: networkError.phase,
          elapsedMs: networkError.elapsedMs,
          headerDeadlineMs: networkError.headerDeadlineMs,
          totalDeadlineMs: networkError.totalDeadlineMs,
          nextHopProtocol: networkError.nextHopProtocol,
          responseStart: networkError.responseStart,
        });
        throw networkError;
      }
      if (signal?.aborted) {
        throw networkError;
      }
      if (shouldRetryApiFetch(method, 0, undefined, attempt, options.skipRetries === true)) {
        if (import.meta.env.DEV) {
          signalDevStackRestarting();
        }
        await sleepMs(apiFetchRetryDelay(attempt));
        continue;
      }
      // fetch rejects when the request never gets a response: server unreachable,
      // restarting mid-deploy, CORS preflight failure, or the network dropped.
      const detail = networkError instanceof Error ? networkError.message : String(networkError);
      const devCrossOrigin =
        import.meta.env.DEV &&
        typeof window !== "undefined" &&
        url.startsWith("http") &&
        new URL(url).origin !== window.location.origin;
      const userMessage =
        devCrossOrigin && method !== "GET" && method !== "HEAD"
          ? "This request was blocked before it reached the hub API. Reload the page after the dev stack restarts, or open the branch on its direct UI port."
          : HUB_UNREACHABLE_MESSAGE;
      logApiErrorForSupport(url, method, 0, userMessage, { detail });
      if (url.startsWith("/api/me")) {
        authLog("api:me:error", { status: 0, message: userMessage, path: url, detail });
      }
      if (!import.meta.env.DEV && !isHubOAuthBannerApiPath(path)) {
        signalHubInfrastructureBlip();
      }
      throw new ApiError(0, userMessage);
    }

    let data: unknown = undefined;
    if (text.length > 0) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }

    if (!response.ok) {
      if (isAuthServiceUnavailablePayload(response.status, data)) {
        reportAuthServiceDegraded(authServiceUnavailableRetryAfterSeconds(data));
      }
      if (shouldRetryApiFetch(method, response.status, data, attempt, options.skipRetries === true)) {
        if (import.meta.env.DEV) {
          signalDevStackRestarting();
        }
        await sleepMs(apiFetchRetryDelay(attempt));
        continue;
      }
      if (url.startsWith("/api/me")) {
        const { message, detail } = parseApiErrorPayload(response.status, data);
        authLog("api:me:error", { status: response.status, message, path: url, detail });
      }
      throwApiError(path, method, response.status, data, options.supportLogContext);
    }
    if (isAuthServiceDegraded()) {
      reportAuthServiceRecovered();
    }
    if (url.startsWith("/api/me")) {
      authLog("api:me:ok", { userId: (data as { user?: { id: string } })?.user?.id });
    }
    const normalizedMethod = method.trim().toUpperCase();
    if (
      normalizedMethod !== "GET"
      && normalizedMethod !== "HEAD"
      && !path.startsWith("/api/labor/heartbeat")
      && !path.startsWith("/api/labor/end-session")
      && !path.startsWith("/api/labor/end-mobile-session")
      && !path.startsWith("/api/hub/presence/")
    ) {
      notifyLaborAttentionActivity();
    }

    return data as T;
  }

  throw new ApiError(0, HUB_UNREACHABLE_MESSAGE);
}

/** Auth-gated binary GET. Returns the raw bytes plus useful response headers. */
export async function apiFetchBlob(
  path: string,
  options: { signal?: AbortSignal } = {},
): Promise<{ blob: Blob; contentType: string; originalFilename: string | null }> {
  const signal = resolveApiFetchAbortSignal("GET", options.signal);
  const headers: Record<string, string> = {
    ...(await authHeader()),
    ...hubOperationalDataSourceRequestHeader(),
    ...standardUserPreviewRequestHeader(),
    ...hubViewAsRequestHeader(),
  };
  const url = hubApiUrl(path);
  const response = await fetch(url, {
    method: "GET",
    headers,
    cache: "no-store",
    signal,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    let data: unknown = text;
    try {
      data = text ? JSON.parse(text) : undefined;
    } catch {
      data = text;
    }
    throwApiError(path, "GET", response.status, data);
  }
  const blob = await response.blob();
  const contentType = response.headers.get("Content-Type") ?? blob.type ?? "application/octet-stream";
  const originalFilename = response.headers.get("X-Original-Filename");
  return { blob, contentType, originalFilename };
}

/**
 * Multipart upload; do not set Content-Type so the browser adds the boundary.
 */
export async function apiUploadFormData<T>(
  path: string,
  formData: FormData,
  options: { timeoutMs?: number; method?: "POST" | "PATCH" | "PUT" } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    ...(await authHeader()),
    ...hubOperationalDataSourceRequestHeader(),
    ...standardUserPreviewRequestHeader(),
    ...hubViewAsRequestHeader(),
  };
  const timeoutMs = options.timeoutMs ?? 120_000;
  const method = options.method ?? "POST";
  const url = hubApiUrl(path);
  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: formData,
      signal: AbortSignal.timeout(timeoutMs),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "TimeoutError") {
      throw new ApiError(0, `Upload timed out after ${Math.round(timeoutMs / 1000)} seconds. Try again or use a smaller file.`);
    }
    throw error;
  }

  const text = await response.text();
  let data: unknown = undefined;
  if (text.length > 0) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!response.ok) {
    throwApiError(path, method, response.status, data);
  }

  return data as T;
}
